import usePageTitle from '../hooks/usePageTitle';
import { BsFillClipboard2PulseFill  } from "react-icons/bs";
import { ReactComponent as DLogo } from '../assets/d_Logo.svg';

import { useCsvTable } from '../hooks/useCsvTable';
import DropZone from '../components/mychart/DropZone';
import MessageList from '../components/mychart/MessageList';
import Toolbar from '../components/mychart/Toolbar';
import DataTable from '../components/mychart/DataTable';
import Pagination from '../components/mychart/Pagination';
import styles from '../components/mychart/App.module.scss';
import StandardLink from '../tools/StandardLink';
//import RangeChart from '../components/mychart/RangeChart';

import("../styles/main.scss");
import("../styles/extension.scss");

function MyChart() {
  usePageTitle('MyChart Labs Director - Browser Extension | Dochano');

  const {
    extraCols, messages, hasData, loading,
    sortCol, sortDir, search, page, totalPages,
    filtered, paginated, uniqueNames,
    handleFile, reset, handleSort, handleSearch, setPage,
  } = useCsvTable();

  const sortHint = sortCol === 'name'
    ? `Sorted by Name (${sortDir === 'asc' ? 'A → Z' : 'Z → A'}), then by Date within the same name`
    : `Sorted by Date (${sortDir === 'asc' ? 'oldest first' : 'newest first'}), then by Name alphabetically`;
/*
  const chartData = [
    { date: "02/16/2018", value: 98,  low: 96, high: 111 },
    { date: "09/02/2020", value: 102, low: 96, high: 111 },
    { date: "11/08/2024", value: 99,  low: 96, high: 111 },
    { date: "04/09/2026", value: 107, low: 96, high: 111 },
    { date: "12/19/2012", value: 105, low: 96, high: 111 },
    { date: "01/26/2021", value: 103, low: 96, high: 111 },
    { date: "05/25/2017", value: 100, low: 96, high: 111 },
    { date: "05/25/2017", value: 109, low: 88, high: 101 },
    { date: "06/27/2019", value: 106, low: 88, high: 101 },
    { date: "01/12/2017", value: 103, low: 88, high: 101 },
    { date: "03/14/2015", value: 115, low: 96, high: 111 }, // above range → red
    { date: "07/10/2023", value: 89,  low: 96, high: 111 }, // below range → red
  ];
  */

  return (
    <section className="extension-container myChart">
      <div id="Intro">
        <div id="smallScreenLogo">
            <DLogo style={{ color: '#ff6123', display: 'inline', margin: '16px'}} width="96px" height="96px" />
            <BsFillClipboard2PulseFill  style={{ color: '#800080', display: 'inline', position: 'relative', top: '-86px', left: '42px'}} size="28px" />
        </div>
        <div id="extensionMarquee">
            <div className="marquee">
                <DLogo width="300px" height="300px" />
                <BsFillClipboard2PulseFill id="cardboardBox" size="4.25em" />
                
                <div id="promotionalText">
                    <span>MyChart Labs Director</span>
                    <span className="promotional-main">take control over your MyChart Lab results</span>
                    <span className="promotional-sub">Download each lab result as CSV to combine and bring to life below</span>
                    <span className="promotional-sub">Upload a CSV with <strong>Name</strong>, <strong>Date</strong> and <strong>Type</strong> columns — sort, search, and browse your data.</span>
                    {!hasData && (
                        <DropZone onFile={handleFile} loading={loading} />
                    )}
    
                    <div className="promotional-sub"><MessageList messages={messages} uniqueNames={uniqueNames} /></div>

                    {hasData && (
                        <button className={styles.clearBtn} onClick={reset} aria-label="Clear file and reset">
                            Clear file and reset
                        </button>
                    )}
                </div>
            </div>   

            <div className="footnote">
                Brought to you by <strong>Dochano</strong>
            </div>
        </div>

      </div>

    {hasData && (
        <section className={styles.tableSection} aria-label="CSV data table">
          <h3>Interactive Lab Results</h3>
          <Toolbar
            search={search}
            onSearch={handleSearch}
            showing={filtered.length}
          />

          <div className={styles.tableCard}>
            <DataTable
              rows={paginated}
              extraCols={extraCols}
              sortCol={sortCol}
              sortDir={sortDir}
              onSort={handleSort}
            />
            <Pagination
              page={page}
              totalPages={totalPages}
              totalRows={filtered.length}
              onPage={setPage}
            />
          </div>
          
          <p className={styles.sortHint}>
            {sortHint}. Table is best viewed in browser width of 845px or greater; otherwise you will see an inner scrollbar.
          </p>

          
          {/* <RangeChart
            data={chartData}
            title="Value Over Time"
            subtitle="Red points fall outside the defined high / low boundary."
          /> */}
        </section>
      )}

    {!hasData && (
    <div id="extensionInfo">
        <h3>About the Extension</h3>
            <p>Many doctors offices make use of MyCharts by Epic. Recently I had to change doctors which ended up using a different MyCharts site. My information was now on two different sites.</p>
            <p>I found the site a bit lacking when it came to lab results. Seemed good to pull up a single reference and the charts will attempt to capture history if you happen to be with the same site.</p>
            <p>For my scenario, I had two different sites with the information and even linking the accounts (see how at <StandardLink href="https://www.mychart.org/HappyTogether" isNewWindow={true}>Happy Together by MyChart</StandardLink>) doesn't allow for Result Trends to consolidate history, it is still segerated.</p>
            <p>It would be nice to have a Lab Results site where you can quickly identify areas of concern along with seeing a history. Add ability to directly learn more about the topic without having to open another pop-up and search for the result you are already viewing.</p>
            <p>NOTE: data is <strong>not stored</strong> and only for educational purposes on what could be possible. Recommended to discuss with your Doctor.</p>
            <p>Used experience from Job Post Extractor for the Extension.</p>
            <p><strong>AI Disclaimer: </strong>Worked with AI to implement CSV conversion to table with sort and search functionality. AI was also used in the Python consolidation work. All other updates were personal updates.</p>
    
        <h3>Enter MyCharts Lab Director</h3>
            <ul>
                <li>Capture each of your results into a CSV file by using this browser extension.</li>
                <li>Open each and consolidate into a single CSV file.</li>
                <li className="subList"><ul>
                    <li>Python users can use the following script to do the combining into a single file. <StandardLink href="https://github.com/grjohnson1/Python_Arena/blob/main/combine_csv.py" isNewWindow={true}>combine_csv.py</StandardLink></li>
                    <li>Requires all the CSV files to be within the same folder and have the header row with the following columns: "Name,Date,Type,Result,Low,High,Description".</li>
                    <li>For reference I had 49 files from different providers that I combined into a single file.</li>
                </ul></li>
                <li>Required to have Name and Date column to render table.</li>
                <li className="subList"><ul>
                    <li>Create a filter of top row and sort on the Name, Date, and Type.</li>
                    <li>Now with Google Sheets and Microsoft Excel you can easily establish formulas and even chart your own.</li>
                </ul></li>
                <li>Upload information into this site for a quick visualization of your data.</li>
                <li className="subList"><ul>
                    <li>Easy to read table with sort and filtering criteria.</li>
                    <li>Click <strong>📋 Load sample data</strong> button to see an example rendering.</li>
                    <li>Inline graph of data will display if Result column is followed with a Low and/or High column. Referencing many years of creating a graph representation of Last Price compared to 52 week high/low for Stocks.</li>
                    <li>Link of Name and Type to get more information. Note that depending on how the name and type is entered will determine if the definition is found.</li>
                    <li><strong>Coming soon:</strong> Historical chart view for multiple results for same name.</li>
                </ul></li>
            </ul>
    </div>
    )}
    </section>
  );
}

export default MyChart;